// @flow
import React from 'react';
import {compose} from 'redux';
import {connect} from 'react-redux';

import Experiment from '@homeaway/react-experiment';
import Filters from '@homeaway/react-search-filters';

import FilterBar from '../FilterBar/FilterBar';
import FilterForm from '../FilterForm/FilterForm';
import FilteredCountDataProvider from '../FilteredCountDataProvider/FilteredCountDataProvider';
import FormModal from '../FormModal/FormModal';
import {media} from '../../utils/breakpoints';
import withLingua from '../withLingua/withLingua';

import routerActions from '../../actions/router';
import overlayActions from '../../actions/overlay';
import searchParamsActions from '../../actions/searchParams';
import {FILTERS_MODAL} from '../../actions/modals';

import type {State as ReduxState} from '../../reducers/index';
import type {Action as OverlayAction} from '../../actions/overlay';
import type {SearchParamsType} from '../../reducers/searchParams';
import type {FilterGroupType} from '../MoreFilters/MoreFilters';
import type {LinguaProps} from '../withLingua/withLingua';

import {VRBO_SERP_FILTER_BAR_REPLACEMENT} from '../../abacus/experiments';

import {
    instantBookFilter,
    isOnlyInstantConfirmationFilter
} from '../../utils/filters';

export type FilterDispatchProps = {
    filter: typeof routerActions.filter,
    onDone: () => void,
    resetInputParams: typeof searchParamsActions.resetInputParams,
    syncInputParams: typeof searchParamsActions.syncInputParams,
    updateFilterCheck: typeof searchParamsActions.updateFilterCheck,
    updateInputParams: typeof searchParamsActions.updateInputParams,
    updatePriceInputValuesValidity: typeof searchParamsActions.updatePriceInputValuesValidity
};

export type FiltersProps = {
    currency: string,
    hasDates: boolean,
    inputParamsAreChanged: boolean,
    inputParamsAreDefault: boolean,
    isMobile: boolean,
    locale: string,
    priceInputValuesAreValid: boolean,
    viewportHeight: number,
    numFiltersPanel?: ?number
};

export type FilterFormProps = {
    filterGroups: FilterGroupType[],
    inputParams: SearchParamsType,
    modalIsOpen: boolean,
    resultsCount?: ?number
} & FiltersProps &
    FilterDispatchProps;

export type FilterBarProps = {
    currentBedrooms: number,
    exactBedroomsMatch: boolean,
    hideOverlay: () => OverlayAction,
    inputMaxBedrooms: number,
    inputMaxPrice: number,
    inputMinBedrooms: number,
    inputMinPrice: number,
    isInstantBookingChecked: boolean,
    isMoreFiltersSelected: boolean,
    isOverlayVisible: boolean,
    maxBedrooms: number,
    maxPrice: number,
    minBedrooms: number,
    minPrice: number,
    searchResultsLoading: boolean,
    showOverlay: () => OverlayAction,
    siteName: string,
    topBannersHeight: number
} & FiltersProps &
    FilterDispatchProps;

export type FilterContainerProps = FilterBarProps &
    FilterFormProps &
    FilterDispatchProps &
    LinguaProps;

export function FiltersContainer(props: FilterContainerProps) {
    return (
        <Experiment name={VRBO_SERP_FILTER_BAR_REPLACEMENT}>
            {({On, Off}) => (
                <React.Fragment>
                    <On>
                        <Filters
                            {...props}
                            filterCountDataProvider={FilteredCountDataProvider}
                            modal={FormModal}
                        />
                    </On>
                    <Off>
                        {props.isMobile ? (
                            <FilterForm {...props} />
                        ) : (
                            <FilterBar {...props} />
                        )}
                    </Off>
                </React.Fragment>
            )}
        </Experiment>
    );
}

export const mapStateToProps = (state: ReduxState) => {
    // select params that only apply to this component
    const {
        searchParams: {
            params,
            params: {
                filter,
                minBedrooms,
                maxBedrooms,
                minBathrooms,
                maxBathrooms,
                minPrice,
                maxPrice
            },
            inputParams,
            inputParamsAreDefault,
            inputParamsAreChanged,
            numFiltersPanel,
            priceInputValuesAreValid,
            selectedFilters
        },
        viewport: {height, breakpoint},
        modals,
        system: {currency, siteName, fullLocale},
        topBanners: {height: topBannersHeight},
        overlay: {showOverlay: isOverlayVisible}
    } = state;
    return {
        modalIsOpen: modals.FILTERS.isOpen,
        isMobile: !media(breakpoint, {minWidth: 'SM_MIN'}),
        canFit3Columns: media(breakpoint, {minWidth: 'MD_MIN'}),
        canFit4Columns: media(breakpoint, {minWidth: 'LG_MIN'}),
        bedroomsExactMatch: inputParams.minBedrooms === inputParams.maxBedrooms,
        params,
        currency,
        hasDates: !!(inputParams.arrival && inputParams.departure),
        minBedrooms,
        maxBedrooms,
        minPrice,
        maxPrice,
        inputParams,
        inputMinBedrooms: inputParams.minBedrooms,
        inputMaxBedrooms: inputParams.maxBedrooms,
        inputMinPrice: inputParams.minPrice,
        inputMaxPrice: inputParams.maxPrice,
        inputParamsAreChanged,
        siteName,
        topBannersHeight,
        inputParamsAreDefault,
        priceInputValuesAreValid,
        isOverlayVisible,
        isInstantBookingChecked: !!instantBookFilter(filter),
        isMoreFiltersSelected: !!(
            (minBathrooms && minBathrooms > 0) ||
            (maxBathrooms && maxBathrooms > 0) ||
            (filter &&
                filter.length > 0 &&
                !isOnlyInstantConfirmationFilter(filter))
        ),
        locale: fullLocale,
        viewportHeight: height,
        selectedFilters,
        numFiltersPanel
    };
};

export const mapDispatchToProps = {
    filter: routerActions.filter,
    hideOverlay: () => overlayActions.toggleOverlay(false),
    resetInputParams: searchParamsActions.resetInputParams,
    showOverlay: () => overlayActions.toggleOverlay(true),
    syncInputParams: searchParamsActions.syncInputParams,
    updateFilterCheck: searchParamsActions.updateFilterCheck,
    updateInputParams: searchParamsActions.updateInputParams,
    updatePriceInputValuesValidity:
        searchParamsActions.updatePriceInputValuesValidity,

    // FilterForm
    onDone: () => routerActions.popModal(FILTERS_MODAL)
};

const ConnectedFiltersContainer = compose(
    connect(mapStateToProps, mapDispatchToProps),
    withLingua({
        namespace: 'traveler/search',
        category: 'messages'
    })
)(FiltersContainer);

export default ConnectedFiltersContainer;
